<template>
  <BgImageLayout>
    <ConfirmationDialog
      ref="confirmDialog"
      title="Confirm"
      confirm-label="Yes, delete"
      confirm-btn-type="danger"
      cancel-label="no, cancel"
    />

    <RModal ref="modal" :title="`Create ${costCenterLabel.singular}`" @cancel="closeModal" @ok="createCostCenter">
      <RInput
        v-model.trim="costCenter"
        autofocus
        class="q-pt-lg"
        :label="costCenterLabel.singular"
        no-hint
        no-feedback-icon
      />
    </RModal>

    <RModal ref="editModal" :title="`Edit ${costCenterLabel.singular}`" @cancel="closeEditModal" @ok="updateCostCenter">
      <RInput
        v-if="costCenterToEdit"
        v-model.trim="costCenterToEdit.name"
        class="q-pt-lg"
        autofocus
        :label="costCenterLabel.singular"
        no-hint
        no-feedback-icon
      />
    </RModal>

    <RModal ref="editEntityModal" title="Edit entity name" @cancel="$event()" @ok="updateEntityName">
      <RInput
        v-model.trim="newEntityName"
        class="q-pt-lg"
        autofocus
        label="Entity name"
        no-hint
        no-feedback-icon
        on-clear="focus"
      />
    </RModal>

    <Grid gtr="auto 1fr" class="full-height">
      <RCard class="cost-centers q-pt-lg">
        <Grid gaf="column" justify-content="space-between" align-items="center">
          <Grid gaf="column" justify-content="start" align-items="center" gap="var(--r-space-sm)">
            <span class="text-heading-3 text-secondary-1">{{ costCenterLabel.plural }}</span>
            <q-btn dense size="0.675rem" flat round icon="edit" color="primary" @click="onEditEntityName">
              <LmTooltip>Edit entity name</LmTooltip>
            </q-btn>
          </Grid>
          <RBtn type="tertiary" size="small" @click="showCreateModal">Create new {{ costCenterLabel.singular }}</RBtn>
        </Grid>
      </RCard>

      <Grid
        v-if="costCenters && costCenters.length"
        justify-content="center"
        align-content="start"
        justify-items="center"
        class="cost-center-items q-mt-md"
      >
        <CostCenter
          v-for="costCenter of costCenters"
          :key="costCenter.id"
          :name="costCenter.name"
          :is-system="costCenter.system"
          @edit="onEdit(costCenter)"
          @delete="onDelete(costCenter.id)"
        />
      </Grid>

      <Grid v-else-if="costCenters && !costCenters.length" place-items="center" place-content="center">
        <NoData />
        <span>There are no {{ costCenterLabel.plural }} created yet</span>
      </Grid>

      <Grid v-else justify-content="center" align-items="center" class="relative-position q-mt-md">
        <QInnerLoading :showing="true">
          <QSpinnerHourglass size="3.5rem" color="primary-1" />
        </QInnerLoading>
      </Grid>
    </Grid>
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { mapGetters } from 'vuex'
// Components
import RModal from '@/ui/RModal'
import CostCenter from './components/CostCenter'
import ConfirmationDialog from '@/ui/ConfirmationDialog'
import NoData from '@/reusable/svg-icons/NoData'

export default {
  components: {
    RModal,
    CostCenter,
    ConfirmationDialog,
    NoData
  },

  data: () => ({
    costCenter: null,
    costCenterToEdit: null,
    costCenters: null,
    newEntityName: null
  }),

  computed: {
    ...mapGetters('entity-labels', ['costCenterLabel'])
  },

  created() {
    apiTo.GET_COST_CENTERS().then(({ data }) => {
      this.costCenters = data.result
    })
  },

  methods: {
    showCreateModal() {
      this.$refs.modal.show()
    },

    closeModal(hideModalFn) {
      hideModalFn()
      this.costCenter = null
    },

    closeEditModal(hideModalFn) {
      hideModalFn()
      this.costCenterToEdit = null
    },

    createCostCenter(hideModal, setModalLoading) {
      if (!this.costCenter || !this.costCenter.trim()) return
      setModalLoading()

      apiTo
        .CREATE_COST_CENTER(this.costCenter)
        .then(({ data }) => {
          this.closeModal(hideModal)
          this.costCenters.push(data.result)
        })
        .finally(() => setModalLoading(false))
    },

    updateCostCenter(hideModal, setModalLoading) {
      if (!this.costCenterToEdit.name || !this.costCenterToEdit.name.trim()) return
      setModalLoading()

      apiTo
        .UPDATE_COST_CENTER(this.costCenterToEdit.id, this.costCenterToEdit.name)
        .then(({ data }) => {
          const index = this.costCenters.findIndex(c => c.id === this.costCenterToEdit.id)

          if (~index) this.costCenters.splice(index, 1, data.result)
          this.closeEditModal(hideModal)
        })
        .finally(() => setModalLoading(false))
    },

    onEdit(costCenter) {
      this.costCenterToEdit = { ...costCenter }
      this.$refs.editModal.show()
    },

    onDelete(costCenterId) {
      apiTo.COST_CENTER_DELETE_CONFIRMATION(costCenterId).then(({ data }) => {
        const body = data.messages.default.join('\n')

        const dialog = this.$refs.confirmDialog.show({ body })
        dialog.onCancel(hide => hide())
        dialog.onConfirm(this.deleteCostCenter.bind(this, costCenterId))
      })
    },

    deleteCostCenter(id, hideModal, setModalLoading) {
      setModalLoading()

      apiTo
        .DELETE_COST_CENTER(id)
        .then(() => {
          hideModal()
          const index = this.costCenters.findIndex(c => c.id === id)

          if (~index) this.costCenters.splice(index, 1)
        })
        .finally(() => setModalLoading(false))
    },

    onEditEntityName() {
      this.newEntityName = this.costCenterLabel.label
      this.$refs.editEntityModal.show()
    },

    updateEntityName(hideModal, setModalLoading) {
      setModalLoading()

      this.$store
        .dispatch('entity-labels/updateEntityLabel', {
          entity: this.costCenterLabel.entity,
          label: this.costCenterLabel,
          newName: this.newEntityName
        })
        .then(hideModal)
        .finally(() => setModalLoading(false))
    }
  }
}
</script>

<style lang="scss" scoped>
.cost-center-items {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--r-space-sp) var(--r-space-md);
}
</style>
